import { CurrencyDropdown } from "components";
import { PhosphorSwapButton } from "components/Atoms/PhosphorIconButton";
import Panel from "components/panels/panel";
import React, { useState } from "react";
import { Message } from "components/Atoms/Message";
import { getExchangeRateFormatter } from "utilities/formatter";
import { useLoaderData } from "react-router";

export const ExchangeRateApp = () => {
  const viewModel = useLoaderData<server.viewmodel.ExchangeRateFrontModel>();

  const { exchangeRates } = viewModel;
  const exchangeRateFormatter = getExchangeRateFormatter();
  const [fromCurrency, setFromCurrency] = useState<server.dto.Currencies>("eur");
  const [toCurrency, setToCurrency] = useState<server.dto.Currencies>("usd");

  const rate = exchangeRates[fromCurrency].find(r => r.to.value === toCurrency);

  const swap = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  }

  return <div className="flex-column gap-bigger">
    <Panel
      panelClassName="s1_panel"
      title={RESX.Financial.ExchangeRates}
    >
      <div className="flex flex-wrap gap align-center margin-bottom">
        <div style={{ width: 300 }}>
          <CurrencyDropdown
            value={fromCurrency}
            onChange={v => setFromCurrency(v)}
          />
        </div>
        {RESX.Message.lblTo}
        <div style={{ width: 300 }}>
          <CurrencyDropdown
            value={toCurrency}
            onChange={v => setToCurrency(v)}
          />
        </div>
        <PhosphorSwapButton
          layout="in-square"
          onClick={swap}
          backgroundColor="light"
          padding="large"
        />
        {!rate && <div>{RESX.GeneralLabels.NotApplicable}</div>}
        {rate && <div>1 {rate.from.text} = {exchangeRateFormatter.format(rate.rate)} {rate.to.text}</div>}
      </div>
    </Panel>

    <ExchangeRateTable viewModel={viewModel} />

    <div style={{ overflow: "hidden" }} className="panel-bg-color">
      <Message type="info">
        <p>{viewModel.disclaimerText}</p>
        <p className="s1_gray">{viewModel.lastUpdated}</p>
      </Message>
    </div>
  </div>
}

interface Props {
  viewModel: server.viewmodel.ExchangeRateFrontModel
}

const ExchangeRateTable = (props: Props) => {
  const { exchangeRates } = props.viewModel;
  const exchangeRateFormatter = getExchangeRateFormatter();

  const columnCurrencies = Object.values(exchangeRates).map(rates => rates[0].from.value);

  return <Panel
    panelClassName="s1_panel"
    contentCssClass="overflow-x"
  >
    <table className="s1_table s1_table_exchangerate">
      <tbody>
        <tr>
          <td />
          {Object.values(exchangeRates).map(rates => <ExchangeRateHeader key={rates[0].from.value} rate={rates[0]} className="s1_cell_financialamount" />)}
        </tr>
        {Object.values(exchangeRates).map(rates => {
          const firstRate = rates[0];

          return <tr key={firstRate.from.value}>
            <ExchangeRateHeader rate={firstRate} />
            {columnCurrencies.map(currency => {
              const rate = rates.find(r => r.to.value === currency) || null;

              if (!rate) {
                return <td key={`${firstRate.from.value}-${currency}`} className="s1_cell_financialamount s1_smaller" />
              }

              return <td
                title={`${rate.from.text}->${rate.to.text}: ${exchangeRateFormatter.format(rate.rate)}`}
                className="s1_cell_financialamount s1_smaller"
                key={`${rate.from.value}-${rate.to.value}`}>
                {rate.from.value === rate.to.value ? "" : exchangeRateFormatter.format(rate.rate)}
              </td>
            })}
          </tr>
        })}
      </tbody>
    </table>
  </Panel>
}

const ExchangeRateHeader = ({ rate, className }: { rate: server.dto.ExchangeRate, className?: string }) => {
  return <td className={className} title={rate.from.name}>{rate.from.text}</td>
}